import { type MouseEvent as ReactMouseEvent } from "react"

import { type OverlayDotBlueprint } from "./uploadedConversationsVisualizerOverlayDots"
import {
  projectToScreen,
  type RgbColor,
} from "./uploadedConversationsVisualizerParticles"

const DOT_VISIBLE_RADIUS = 0.58
const DOT_RING_STROKE_WIDTH = 0.44
const DOT_HIGHLIGHT_RADIUS = DOT_VISIBLE_RADIUS * 1.75
const DOT_HIGHLIGHT_STROKE_WIDTH = DOT_RING_STROKE_WIDTH * 1.75
const DOT_HIT_RADIUS = 2.2

interface UploadedConversationsVisualizerDotFieldProps {
  activeAnomalyMarkerColor?: string | null
  dots: OverlayDotBlueprint[]
  highlightedConversationIds?: ReadonlySet<string> | null
  isAmbientLensSelected?: boolean
  isSelectionLensSelected?: boolean
  onConversationClick?: (conversationId: string) => void
  onDotMouseMove: (
    dot: OverlayDotBlueprint,
    event: ReactMouseEvent<SVGCircleElement>,
  ) => void
  sliceMarkPalette: string[]
}

function resolveFallbackRingColor(color: string | undefined): string {
  if (typeof color === "string" && color.length > 0) {
    return color
  }

  return "rgb(128, 128, 128)"
}

export function resolveRgbString(color: RgbColor) {
  return `rgb(${color.r}, ${color.g}, ${color.b})`
}

export function UploadedConversationsVisualizerDotField({
  activeAnomalyMarkerColor,
  dots,
  highlightedConversationIds,
  isAmbientLensSelected = false,
  isSelectionLensSelected = false,
  onConversationClick,
  onDotMouseMove,
  sliceMarkPalette,
}: UploadedConversationsVisualizerDotFieldProps) {
  const hasConversationHighlightFilter =
    Boolean(highlightedConversationIds) &&
    (highlightedConversationIds?.size ?? 0) > 0
  const dotsToRender =
    isSelectionLensSelected && hasConversationHighlightFilter
      ? [
          ...dots.filter(
            (dot) => !(highlightedConversationIds?.has(dot.dotId) ?? false),
          ),
          ...dots.filter((dot) => highlightedConversationIds?.has(dot.dotId)),
        ]
      : dots

  return (
    <g data-testid="uploaded-conversations-conversation-dots">
      {dotsToRender.map((dot) => {
        const isHighlightedConversation =
          highlightedConversationIds?.has(dot.dotId) ?? false
        const isDeemphasizedConversation =
          hasConversationHighlightFilter && !isHighlightedConversation
        const handleConversationClick = () => {
          if (dot.conversation.isSelectable !== false) {
            onConversationClick?.(dot.conversation.id)
          }
        }
        const ringColor = isAmbientLensSelected
          ? "var(--surface-contrast)"
          : isSelectionLensSelected && isHighlightedConversation
            ? (activeAnomalyMarkerColor ??
              sliceMarkPalette[
                dot.sliceIndex % Math.max(sliceMarkPalette.length, 1)
              ])
            : sliceMarkPalette[
                dot.sliceIndex % Math.max(sliceMarkPalette.length, 1)
              ]

        return (
          <g key={dot.dotId}>
            <circle
              cx={projectToScreen(dot.anchorU, dot.anchorV, 100, 100).x}
              cy={projectToScreen(dot.anchorU, dot.anchorV, 100, 100).y}
              r={
                hasConversationHighlightFilter && isHighlightedConversation
                  ? DOT_HIGHLIGHT_RADIUS
                  : DOT_VISIBLE_RADIUS
              }
              fill="var(--coval-text-muted)"
              fillOpacity={isDeemphasizedConversation ? 0.62 : 1}
              stroke={resolveFallbackRingColor(ringColor)}
              strokeOpacity={isDeemphasizedConversation ? 0.55 : 1}
              strokeWidth={
                hasConversationHighlightFilter && isHighlightedConversation
                  ? DOT_HIGHLIGHT_STROKE_WIDTH
                  : DOT_RING_STROKE_WIDTH
              }
              className={
                dot.conversation.isSelectable === false
                  ? ""
                  : isSelectionLensSelected
                    ? "cursor-pointer transition-[r,stroke,stroke-width] duration-300 ease-out motion-reduce:transition-none"
                    : "cursor-pointer"
              }
              data-conversation-highlighted={
                isHighlightedConversation ? "true" : "false"
              }
              data-conversation-visible-dot="true"
              data-conversation-visible-dot-id={dot.dotId}
              onMouseEnter={(event) => {
                onDotMouseMove(dot, event)
              }}
              onMouseMove={(event) => {
                onDotMouseMove(dot, event)
              }}
              onClick={handleConversationClick}
            />
            <circle
              cx={projectToScreen(dot.anchorU, dot.anchorV, 100, 100).x}
              cy={projectToScreen(dot.anchorU, dot.anchorV, 100, 100).y}
              r={DOT_HIT_RADIUS}
              fill="transparent"
              pointerEvents="all"
              className={
                dot.conversation.isSelectable === false ? "" : "cursor-pointer"
              }
              data-conversation-dot="true"
              data-conversation-hit-dot-id={dot.dotId}
              data-testid={`uploaded-conversations-conversation-dot-hit-${dot.dotId}`}
              onMouseEnter={(event) => {
                onDotMouseMove(dot, event)
              }}
              onMouseMove={(event) => {
                onDotMouseMove(dot, event)
              }}
              onClick={handleConversationClick}
            />
          </g>
        )
      })}
    </g>
  )
}
